import { dayLabel, todayStr } from "../domain/date";
import { dayMark } from "../domain/dayMarks";
import { fastDayRule } from "../domain/eating";
import { useKaya } from "../store/kayaStore";
import { setFastDay, setUntrackedDay } from "../store/toggles";
import { I } from "./icons";
import { NavRow, RowGroup } from "./Rows";
import { Sheet } from "./Sheet";
import { ActionBar } from "./ActionBar";

/**
 * "This day" (board FoodDaySheetMarks; ADR-049): the two marks a day can carry, as switches.
 * Untracked keeps the day out of the averages without deleting what was logged; a fast day takes
 * no deficit, is kept out of the averages and is never a missed day. Written only through
 * `store/toggles.ts`, which stamps both ways and keeps one mark to a day.
 */
export function DayMarkRows({ ds }: { ds: string }) {
  const { S, update } = useKaya();
  const mark = dayMark(S, ds);
  const today = ds === todayStr();
  const when = today ? "today" : "that day";
  const rule = fastDayRule(S);
  return (
    <RowGroup heading="This day">
      <NavRow
        kind="toggle"
        icon={I.list}
        title={today ? "Untracked today" : "Untracked"}
        subtitle={`Some of ${today ? "today" : "the day"} won’t be logged. Kept out of the weekly averages; what you logged stays.`}
        checked={mark === "untracked"}
        onChange={(on) => update((d) => setUntrackedDay(d, ds, on))}
      />
      <NavRow
        kind="toggle"
        icon={I.calendar}
        title="Fast day"
        subtitle={
          rule.keepMeals
            ? `A fast you chose. No deficit ${when}, and no meal skipped; kept out of the averages, never counted as missed.`
            : `A fast you chose. No deficit ${when}; kept out of the averages, never counted as missed.`
        }
        checked={mark === "fast"}
        onChange={(on) => update((d) => setFastDay(d, ds, on))}
      />
    </RowGroup>
  );
}

/** The marks alone, for a past day opened from Progress › Food › By week — the day sheet in Food
 *  reaches today and the week ahead only. */
export function DayMarksSheet({ ds, onClose }: { ds: string; onClose: () => void }) {
  return (
    <Sheet
      title={dayLabel(ds)}
      sub="A marked day is kept out of the weekly averages"
      onClose={onClose}
      footer={<ActionBar inSheet primary={{ label: "Done", onClick: onClose }} />}
    >
      <DayMarkRows ds={ds} />
    </Sheet>
  );
}
